import React from 'react';
import { AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDelta, getImpactTone, getModuleDetailLines, getModuleFailedChecks, getScoreTone } from '../utils/formatters';

const FullReportPhase = ({ fullReport, atRiskPillars, winningPillars, resetAll }) => {
  const overallTone = getScoreTone(fullReport.overall);

  return (
    <motion.div key="full" className="report-stage final-report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="report-header">
        <Lock size={36} className="check-icon" />
        <h3>Full competitor report unlocked</h3>
      </div>

      <div className="report-summary">
        {/* Executive snapshot — score showcase + headline KPIs */}
        <section className="grade-hero">
          <div className={`grade-ring score-${overallTone}`} style={{ '--grade-pct': fullReport.overall }}>
            <span className="grade-ring__value">{fullReport.overall}</span>
            <span className="grade-ring__grade">{fullReport.letterGrade}</span>
            <span className="grade-ring__label">of 100</span>
          </div>
          <div className="grade-hero__body">
            <h4 className="grade-hero__title">Executive snapshot</h4>
            <p className="grade-hero__sub">
              You rank <strong>#{fullReport.rank?.position} of {fullReport.rank?.total}</strong> in your market — {fullReport.percentile}th percentile ({fullReport.tier}).
            </p>
            <div className="kpi-chip-row">
              <div className={`kpi-chip score-${overallTone}`}>
                <small>Overall grade</small>
                <strong>{fullReport.overall}/100 ({fullReport.letterGrade})</strong>
              </div>
              <div className="kpi-chip">
                <small>Rank</small>
                <strong>#{fullReport.rank?.position} of {fullReport.rank?.total}</strong>
              </div>
              <div className="kpi-chip">
                <small>Percentile</small>
                <strong>{fullReport.percentile}th ({fullReport.tier})</strong>
              </div>
              <div className="kpi-chip">
                <small>Gap to top quartile</small>
                <strong>+{fullReport.rank?.deltaToTopQuartile || 0} pts</strong>
              </div>
              <div className="kpi-chip">
                <small>Gap to #1</small>
                <strong>+{fullReport.rank?.deltaToLeader || 0} pts</strong>
              </div>
            </div>
          </div>
        </section>

        {/* League table */}
        <section className="report-section">
          <p className="report-section__title">League table — top competitors</p>
          <ul className="competitor-unlock-list">
            {(fullReport.leagueTable || []).slice(0, 6).map((competitor) => (
              <li key={competitor.id || competitor.name} className={`score-${getScoreTone(competitor.finalScore)}`}>
                <strong>#{competitor.rank} {competitor.name}</strong>
                <span className="league-score">Score {competitor.finalScore} · {competitor.percentile}th percentile</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Pillar comparison */}
        <section className="report-section">
          <p className="report-section__title">Pillar comparison — you vs top cohort average</p>
          {atRiskPillars.length > 0 && (
            <div className="pillar-group-block at-risk">
              <p className="pillar-group-title"><strong>At Risk</strong></p>
              <ul className="competitor-unlock-list">
                {atRiskPillars.map((pillar) => (
                  <li key={pillar.key} className={`score-${pillar.tone}`}>
                    <strong>{pillar.label}</strong>
                    <span>
                      {pillar.value}/100 vs {pillar.hasCohort ? `${pillar.cohortValue}/100` : 'Insufficient data'}
                    </span>
                    {pillar.hasCohort && <small className={`delta-indicator score-${pillar.tone}`}>{formatDelta(pillar.delta)} pts</small>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {winningPillars.length > 0 && (
            <div className="pillar-group-block winning">
              <p className="pillar-group-title"><strong>Winning</strong></p>
              <ul className="competitor-unlock-list">
                {winningPillars.map((pillar) => (
                  <li key={pillar.key} className={`score-${pillar.tone}`}>
                    <strong>{pillar.label}</strong>
                    <span>
                      {pillar.value}/100 vs {pillar.hasCohort ? `${pillar.cohortValue}/100` : 'Insufficient data'}
                    </span>
                    {pillar.hasCohort && <small className={`delta-indicator score-${pillar.tone}`}>{formatDelta(pillar.delta)} pts</small>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Advanced module comparison */}
        {fullReport.modules?.target && (
          <section className="report-section">
            <p className="report-section__title">Advanced module comparison</p>
            <ul className="competitor-unlock-list">
              <li className={`score-${getScoreTone(fullReport.modules.target.reviewSentiment?.score || 0)}`}>
                <strong>Review sentiment</strong>
                <span>
                  {fullReport.modules.target.reviewSentiment?.score || 0}/100 vs {fullReport.modules.competitorAverage?.reviewSentiment?.score ?? 'N/A'}/100
                </span>
              </li>
              <li className={`score-${getScoreTone(fullReport.modules.target.photoQuality?.score || 0)}`}>
                <strong>Photo quality</strong>
                <span>
                  {fullReport.modules.target.photoQuality?.score || 0}/100 vs {fullReport.modules.competitorAverage?.photoQuality?.score ?? 'N/A'}/100
                </span>
              </li>
              <li>
                <strong>Photo count</strong>
                <span>
                  {fullReport.modules.target.photoQuality?.photoCount || 0} vs {fullReport.modules.competitorAverage?.photoQuality?.photoCount ?? 'N/A'}
                </span>
              </li>
            </ul>
          </section>
        )}

        {/* Top opportunities */}
        <section className="report-section">
          <p className="report-section__title">Top opportunities — impact × effort</p>
          <ul className="preview-issues-list">
            {(fullReport.opportunities || []).map((item) => (
              <li key={item.title} className={`opportunity-card impact-${getImpactTone(item.impact)}`}>
                <AlertTriangle size={14} />
                <div>
                  <strong>{item.title}</strong>
                  <div className="signal-chip-row" aria-label="Opportunity impact and effort">
                    <span className={`signal-chip impact-${getImpactTone(item.impact)}`}>{item.impact} impact</span>
                    <span className="signal-chip effort-chip">{item.effort} effort</span>
                  </div>
                  <p>{item.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Detailed module audit */}
        {Array.isArray(fullReport.moduleFindings) && fullReport.moduleFindings.length > 0 && (
          <section className="report-section">
            <p className="report-section__title">Detailed module audit checks</p>
            <ul className="preview-issues-list">
              {fullReport.moduleFindings.map((module) => {
                const checks = Array.isArray(module?.checks) ? module.checks : [];
                const passed = checks.filter((item) => item?.pass).length;
                const failedChecks = getModuleFailedChecks(module);
                const detailLines = getModuleDetailLines(module);
                const score = Number(module.score || 0);
                return (
                  <li key={module.key || module.title} className={`module-audit-card score-${getScoreTone(score)}`}>
                    {failedChecks.length > 0 ? <AlertTriangle size={14} className="issue-icon-fail" /> : <CheckCircle2 size={14} />}
                    <div>
                      <strong>{module.title || module.key} · <span className={`score-value score-${getScoreTone(score)}`}>{module.score || 0}/100</span></strong>
                      <p>{module.ai?.summary || module.summary || 'In-depth checks completed for this section.'}</p>
                      <div className="module-key-points">
                        <span><strong>Top risk:</strong> {module.ai?.topRisk || 'No major blockers detected from current evidence.'}</span>
                        <span><strong>Best action:</strong> {module.ai?.bestAction || 'Maintain current section performance and monitor monthly.'}</span>
                      </div>
                      <p>Checks passed: {passed}/{checks.length}</p>
                      {failedChecks.length > 0 && (
                        <p className="failed-checks-line"><strong>Top failed checks:</strong> {failedChecks.map((item) => item.label).join(' · ')}</p>
                      )}
                      {detailLines.slice(0, 2).map((line) => (
                        <p key={line}>{line}</p>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* AI strategic insight */}
        {fullReport.aiInsights?.summary && (
          <section className="report-section">
            <p className="report-section__title">AI strategic insight</p>
            <ul className="preview-issues-list ai-insight-list">
              <li className="ai-summary-card">
                <CheckCircle2 size={14} />
                <div>
                  <strong>{fullReport.aiInsights.summary}</strong>
                  <p>{fullReport.aiInsights.competitiveNarrative}</p>
                </div>
              </li>
            </ul>

            {Array.isArray(fullReport.aiInsights.riskFlags) && fullReport.aiInsights.riskFlags.length > 0 && (
              <>
                <p className="report-block-title"><strong>AI risk flags</strong></p>
                <div className="signal-chip-row" aria-label="AI risk flags">
                  {fullReport.aiInsights.riskFlags.slice(0, 6).map((risk) => (
                    <span key={risk} className="signal-chip risk-chip">{risk}</span>
                  ))}
                </div>
              </>
            )}

            {Array.isArray(fullReport.aiInsights.coaching) && fullReport.aiInsights.coaching.length > 0 && (
              <>
                <p className="report-block-title"><strong>AI coaching priorities</strong></p>
                <div className="signal-chip-row" aria-label="AI coaching priorities">
                  {fullReport.aiInsights.coaching.slice(0, 4).map((tip) => (
                    <span key={tip} className="signal-chip warning-chip">{tip}</span>
                  ))}
                </div>
              </>
            )}

            {Array.isArray(fullReport.aiInsights.linkedActions) && fullReport.aiInsights.linkedActions.length > 0 && (
              <>
                <p className="report-block-title"><strong>Linked actions</strong></p>
                <div className="signal-chip-row" aria-label="Linked actions">
                  {fullReport.aiInsights.linkedActions.slice(0, 6).map((action) => (
                    <span key={action} className="signal-chip action-chip">{action}</span>
                  ))}
                </div>
              </>
            )}
          </section>
        )}
      </div>

      <button className="btn-try-again secondary" type="button" onClick={resetAll}>Start new scan</button>
    </motion.div>
  );
};

export default FullReportPhase;
